'use client'

import { signOut, useSession } from 'next-auth/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ACCESS_TOKEN_TTL_SEC, REFRESH_TOKEN_TTL_SEC } from '@/lib/auth'
import { setSessionRefreshInProgress } from '@/lib/session-refresh-state'

const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || '/hosting-control-panel'

/** No mouse / touch activity for this long → session timeout dialog (matches refresh/session window). */
const IDLE_THRESHOLD_MS = REFRESH_TOKEN_TTL_SEC * 1000

/** Refresh the access token when this much lifetime remains (works for background/idle tabs too). */
const EXTEND_WHEN_REMAINING_MS = 5 * 60 * 1000

const SILENT_EXTEND_COOLDOWN_MS = 90 * 1000

/** Idle timeout dialog: sign out if the user does not choose to continue. */
const IDLE_DIALOG_AUTO_SIGNOUT_MS = 3 * 60 * 1000

const TICK_MS = 5000

export function SessionIdleWarning() {
    const { data: session, status, update } = useSession()
    const [open, setOpen] = useState(false)
    const [extending, setExtending] = useState(false)
    const [forcedByUnauthorized, setForcedByUnauthorized] = useState(false)

    const expiresAt = (session as { accessTokenExpiresAt?: number | null })?.accessTokenExpiresAt ?? null
    const canExtend = (session as { canExtendSession?: boolean })?.canExtendSession === true

    const lastActivityRef = useRef<number | null>(null)
    const lastSilentExtendAtRef = useRef(0)
    const silentExtendInFlightRef = useRef(false)
    const signOutStartedRef = useRef(false)

    useEffect(() => {
        signOutStartedRef.current = false
        setForcedByUnauthorized(false)
    }, [expiresAt])

    useEffect(() => {
        const onForcedWarning = () => {
            setForcedByUnauthorized(true)
            setOpen(true)
        }
        window.addEventListener('cloudaxe-session-warning', onForcedWarning)
        return () => window.removeEventListener('cloudaxe-session-warning', onForcedWarning)
    }, [])

    const performExtend = useCallback(async (): Promise<'ok' | 'rate_limited' | 'failed'> => {
        setSessionRefreshInProgress(true)
        try {
            const res = await fetch(`${BASE_PATH}/api/auth/extend-session`, {
                method: 'POST',
                credentials: 'include',
            })
            const data = (await res.json()) as {
                success?: boolean
                accessToken?: string
                expiresIn?: number
                error?: string
            }
            if (res.status === 429) {
                return 'rate_limited'
            }
            if (!res.ok || !data?.success || !data?.accessToken) {
                return 'failed'
            }
            const expiresIn = typeof data.expiresIn === 'number' ? data.expiresIn : ACCESS_TOKEN_TTL_SEC
            await update({
                accessToken: data.accessToken,
                expiresIn,
            })
            return 'ok'
        } finally {
            setSessionRefreshInProgress(false)
        }
    }, [update])

    const tryExtend = useCallback(async () => {
        setExtending(true)
        try {
            const result = await performExtend()
            if (result === 'rate_limited') {
                // Keep session; user can retry after the rate-limit window.
                return
            }
            if (result !== 'ok') {
                await signOut({ redirect: true, callbackUrl: `${BASE_PATH}/auth/login?sessionExpired=1` })
                return
            }
            setForcedByUnauthorized(false)
            setOpen(false)
            lastActivityRef.current = Date.now()
        } catch {
            await signOut({ redirect: true, callbackUrl: `${BASE_PATH}/auth/login?sessionExpired=1` })
        } finally {
            setExtending(false)
        }
    }, [performExtend])

    /** Product requirement: only mouse / touch; keyboard alone does not reset idle. */
    useEffect(() => {
        const bump = () => {
            lastActivityRef.current = Date.now()
        }
        lastActivityRef.current = Date.now()
        const opts = { capture: true, passive: true } as const
        window.addEventListener('mousemove', bump, opts)
        window.addEventListener('mousedown', bump, opts)
        window.addEventListener('touchstart', bump, opts)
        return () => {
            window.removeEventListener('mousemove', bump, opts)
            window.removeEventListener('mousedown', bump, opts)
            window.removeEventListener('touchstart', bump, opts)
        }
    }, [])

    const maybeSilentExtend = useCallback(async () => {
        if (!canExtend || silentExtendInFlightRef.current) return
        const now = Date.now()
        if (now - lastSilentExtendAtRef.current < SILENT_EXTEND_COOLDOWN_MS) return
        if (expiresAt == null || expiresAt <= 0) return
        if (expiresAt - now > EXTEND_WHEN_REMAINING_MS) return

        silentExtendInFlightRef.current = true
        lastSilentExtendAtRef.current = now
        try {
            const result = await performExtend()
            if (result === 'ok') {
                setForcedByUnauthorized(false)
                setOpen(false)
            } else {
                // rate_limited or failed: allow another silent attempt after cooldown
                lastSilentExtendAtRef.current = 0
            }
        } finally {
            silentExtendInFlightRef.current = false
        }
    }, [canExtend, expiresAt, performExtend])

    useEffect(() => {
        if (status !== 'authenticated') {
            return
        }

        const tick = () => {
            const now = Date.now()
            if (lastActivityRef.current == null) {
                lastActivityRef.current = now
                return
            }
            const idleFor = now - lastActivityRef.current
            const isIdle = idleFor >= IDLE_THRESHOLD_MS

            if (isIdle && !signOutStartedRef.current && !open) {
                setForcedByUnauthorized(false)
                setOpen(true)
            }

            if (expiresAt == null || expiresAt <= 0) {
                return
            }

            const tokenExpired = now >= expiresAt

            if (tokenExpired) {
                if (!canExtend) {
                    if (!signOutStartedRef.current) {
                        signOutStartedRef.current = true
                        void signOut({ redirect: true, callbackUrl: `${BASE_PATH}/auth/login?sessionExpired=1` })
                    }
                    return
                }
                // Refresh even when idle / backgrounded so leaving a tab does not force logout.
                void maybeSilentExtend()
                return
            }

            void maybeSilentExtend()
        }

        tick()
        const id = setInterval(tick, TICK_MS)
        return () => clearInterval(id)
    }, [status, expiresAt, open, canExtend, maybeSilentExtend])

    useEffect(() => {
        if (!open || forcedByUnauthorized) {
            return
        }
        const timeoutId = window.setTimeout(() => {
            if (!signOutStartedRef.current) {
                signOutStartedRef.current = true
                void signOut({ redirect: true, callbackUrl: `${BASE_PATH}/auth/login?sessionExpired=1` })
            }
        }, IDLE_DIALOG_AUTO_SIGNOUT_MS)
        return () => window.clearTimeout(timeoutId)
    }, [open, forcedByUnauthorized])

    if (status !== 'authenticated') {
        return null
    }

    if (!open) {
        return null
    }

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4" role="alertdialog" aria-modal="true" aria-labelledby="session-expiry-title">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                <h2 id="session-expiry-title" className="text-lg font-semibold text-gray-900">
                    Session expiring soon
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                    {forcedByUnauthorized
                        ? canExtend
                            ? 'Your session is no longer valid for API requests. Stay logged in to refresh your session, or sign out.'
                            : 'Your session is no longer valid. Sign in again to continue.'
                        : canExtend
                          ? 'You have been inactive for 7 days. Stay logged in to continue your session, or sign out.'
                          : 'You have been inactive for 7 days. Sign in again to continue.'}
                </p>
                <div className="mt-6 flex flex-wrap justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => void signOut({ redirect: true, callbackUrl: `${BASE_PATH}/auth/login?sessionExpired=1` })}
                        className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Sign out now
                    </button>
                    {canExtend ? (
                        <button
                            type="button"
                            disabled={extending}
                            onClick={() => void tryExtend()}
                            className="rounded-xl bg-gradient-to-r from-sky-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-md hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sky-500"
                        >
                            {extending ? 'Extending…' : 'Stay logged in'}
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    )
}
